import { Select } from '@pankod/refine-antd'
import React, { Ref, useEffect, useRef, useState } from 'react'
import { BaseSelectRef } from 'rc-select'
import { BaseRecord, GetOneResponse } from '@pankod/refine-core'
import { useDebounce } from 'usehooks-ts'

import { TCommonError } from 'src/interfaces/common'
import useSelectWithMeta from 'src/hooks/useSelectWithMeta'

type InfiniteSelectProps<T> = {
  resource: string
  optionValue: string
  optionLabel: string
  value?: string | number
  onChange?: (value: string | number) => void
  onSelect?: (data?: T) => void
  mapValue?: (data: GetOneResponse<T>['data']) => {
    label: string
    value: string | number
  }
  visible?: boolean
  disabled?: boolean
  searchParams?: string
  placeholder?: string
  initialOptionValue?: { label: string; value: string | number }
}
export const InfiniteSelect = <T extends BaseRecord>({
  visible: rawVisible = true,
  resource,
  optionLabel,
  optionValue,
  mapValue,
  disabled,
  searchParams,
  initialOptionValue,
  ...props
}: InfiniteSelectProps<T>) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const visible = useDebounce(rawVisible)
  const selectRef = useRef<Ref<BaseSelectRef>>()
  const initialOptions = useRef<{ label: string; value: string | number }[]>(
    initialOptionValue ? [initialOptionValue] : [],
  )
  const [options, setOptions] = useState(initialOptions.current)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (!initialOptionValue) return
    initialOptions.current = [initialOptionValue]
    setOptions((prev) => {
      if (prev.some((o) => o.value === initialOptionValue.value)) return prev
      return [initialOptionValue, ...prev]
    })
  }, [initialOptionValue, initialOptionValue?.value])
  const { selectProps, selectQueryResult } = useSelectWithMeta<T, TCommonError>(
    {
      resource,
      optionValue,
      optionLabel,
      defaultValueQueryOptions: {
        enabled: false,
      },
      filters: [
        {
          field: 'pageSize',
          operator: 'eq',
          value: 10,
        },
        {
          field: 'page',
          operator: 'eq',
          value: currentPage,
        },
        ...(searchParams
          ? [
              {
                field: searchParams,
                operator: 'eq' as const,
                value: debouncedSearch,
              },
            ]
          : []),
      ],
      queryOptions: {
        enabled: !!visible && !disabled,
        onSuccess: (data) => {
          const parsedData = (
            mapValue
              ? data.data.map(mapValue)
              : data.data.map((value) => ({
                  label: value[optionLabel],
                  value: value[optionValue],
                  data: value,
                }))
          ).filter((d) => d.value !== props.value)
          setOptions((value) => [...value, ...parsedData])
        },
        keepPreviousData: true,
      },
      dataProviderName: 'lms',
    },
  )
  useEffect(() => {
    setOptions(initialOptions.current)
    setCurrentPage(1)
    // @ts-expect-error
    selectRef.current?.scrollTo(0)
  }, [debouncedSearch, initialOptionValue])

  useEffect(() => {
    if (!visible) {
      setOptions(initialOptions.current)
      setCurrentPage(1)
      // @ts-expect-error
      selectRef.current?.scrollTo(0)
    }
  }, [visible])

  const isLastPage = selectQueryResult.data?.meta
    ? selectQueryResult.data?.meta.currentPage !==
      selectQueryResult.data?.meta.totalPages
    : selectQueryResult.data?.next

  return (
    // @ts-expect-error
    <Select
      {...props}
      {...selectProps}
      ref={selectRef.current}
      options={options}
      showSearch={!!searchParams}
      onSearch={(text) => setSearch(text)}
      onPopupScroll={({ target }) => {
        const scrollHeight = (target as HTMLDivElement).scrollHeight
        const containerHeight =
          (target as HTMLDivElement).scrollTop +
          (target as HTMLDivElement).offsetHeight
        const hasReachBottom = scrollHeight === containerHeight
        if (
          !selectQueryResult.isFetching &&
          !selectQueryResult.isError &&
          isLastPage &&
          hasReachBottom
        ) {
          setCurrentPage((page) => ++page)
        }
      }}
      disabled={disabled}
      loading={selectQueryResult.isFetching}
      onSelect={(id: unknown) =>
        props.onSelect?.(
          options.find(({ value }) => id === value) as T | undefined,
        )
      }
    />
  )
}
